import {
  DropDownListChangeEvent,
  DropDownListProps,
  DropDownList as KendoDropdown,
} from '@progress/kendo-react-dropdowns';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { ValidationError } from '../../apps/benefit/models/Errors';
import useToggleClass from '../hooks/useToggleClass';
import ErrorViewer from './ErrorViewer';

interface DropdownCustomProps {
  controlClassName?: string;
  popupClassName?: string;
  mandatory?: boolean;
  helper?: string;
  errors?: ValidationError[];
  clearErrors?: () => void;
  onClear?: () => void;
}

type DropdownProps = DropdownCustomProps & DropDownListProps;

export default function Dropdown(props: DropdownProps) {
  const {
    errors,
    onChange,
    clearErrors,
    onClear,
    label,
    id,
    controlClassName,
    popupClassName,
    mandatory,
    helper,
    filterable,
    value,
  } = props;
  const { addClass, removeClass } = useToggleClass();
  const componentRef = useRef<HTMLDivElement | null>(null);

  const internalOnChange = (e: DropDownListChangeEvent) => {
    if (errors?.length) {
      clearErrors?.();
    }
    onChange?.(e);
  };

  const handleClear = () => {
    if (errors?.length) {
      clearErrors?.();
    }
    onClear?.();
  };

  const classNames = `control-group ${controlClassName ?? ''}`.trim();

  const popupSettings = {
    className: `${popupClassName ?? ''} ${
      filterable ? 'dropdown-filterable' : ''
    }`,
  };

  useEffect(() => {
    errors && errors?.length > 0
      ? addClass('control-error', componentRef)
      : removeClass('control-error', componentRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const hasValue = value !== null && value !== undefined && value !== '';

  return (
    <div className={classNames} ref={componentRef}>
      <div className="control-content">
        {!!label && (
          <label htmlFor={id}>
            {label}
            {mandatory && <sup>*</sup>}
          </label>
        )}
        <div
          className={`dropdown-container ${
            hasValue && onClear ? 'has-clear' : ''
          }`}
        >
          <KendoDropdown
            {..._.omit(props, [
              'label',
              'popupSettings',
              'mandatory',
              'clearErrors',
              'onClear',
            ])}
            onChange={internalOnChange}
            popupSettings={popupSettings}
          />
          {hasValue && onClear && (
            <button
              type="button"
              onClick={handleClear}
              className="dropdown-clear"
            ></button>
          )}
        </div>
      </div>
      {!!helper && <div className="control-info">{helper}</div>}
      <ErrorViewer errors={errors} />
    </div>
  );
}
